import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function CloseAccountBlock() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [owned, setOwned] = useState([]);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleteOrphans, setDeleteOrphans] = useState(false);
  const [busy, setBusy] = useState(false);

  const openFlow = async () => {
    try {
      const { data } = await api.get("/me/owned-workspaces");
      setOwned(data || []);
      setOpen(true);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not start")); }
  };

  const transfer = async (wsId, newOwnerId) => {
    try {
      await api.post(`/workspaces/${wsId}/transfer-ownership`, { new_owner_id: newOwnerId });
      toast.success("Ownership transferred");
      const { data } = await api.get("/me/owned-workspaces");
      setOwned(data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Transfer failed")); }
  };

  const closeAccount = async () => {
    if (confirmEmail.toLowerCase() !== (user?.email || "").toLowerCase()) {
      toast.error("Type your email exactly to confirm."); return;
    }
    if (!window.confirm("Final warning — your account will be permanently deleted. Continue?")) return;
    setBusy(true);
    try {
      await api.delete("/me", { data: { confirm_email: confirmEmail, delete_orphan_workspaces: deleteOrphans } });
      toast.success("Account closed");
      await logout?.();
      window.location.href = "/login";
    } catch (e) { toast.error(apiErrorMessage(e, "Could not close account")); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-t border-red-500/30 pt-4 space-y-3" data-testid="close-account-block">
      <div className="flex items-start gap-2">
        <Trash2 className="w-4 h-4 text-red-500 mt-0.5" />
        <div>
          <div className="font-semibold text-sm text-red-600">Close my account</div>
          <p className="text-xs text-foreground/70 mt-1 max-w-xl">
            Permanently deletes your user profile and all workspace memberships. If you own workspaces you must
            transfer ownership (or delete them) first — we&apos;ll walk you through it.
          </p>
        </div>
      </div>
      <Button type="button" variant="outline" className="rounded-none border-red-500/40 text-red-600 hover:text-red-600" onClick={openFlow} data-testid="close-account-btn">
        Close my account…
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="close-account-dialog">
          <DialogHeader><DialogTitle className="text-red-600">Close my account</DialogTitle></DialogHeader>
          {owned.length > 0 && (
            <div className="space-y-3">
              <div className="label-eyebrow">Workspaces you own</div>
              <p className="text-xs text-foreground/70">Each workspace below still has you as the owner. Transfer to a teammate or tick the box below to delete them outright.</p>
              {owned.map((ws) => (
                <div key={ws.id} className="border border-border p-4" data-testid={`close-account-ws-${ws.id}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div><strong>{ws.name}</strong> <span className="text-[11px] text-foreground/50 font-mono">{ws.slug}</span></div>
                  </div>
                  {ws.transfer_candidates.length === 0 ? (
                    <div className="text-xs text-foreground/60">No other members — you&apos;ll need to either invite someone, or include this workspace in the deletion below.</div>
                  ) : (
                    <div className="space-y-1">
                      {ws.transfer_candidates.slice(0, 5).map((c) => (
                        <div key={c.id} className="flex items-center justify-between text-xs" data-testid={`close-account-candidate-${c.id}`}>
                          <div><strong>{c.name || c.email}</strong> <span className="text-foreground/50">· {c.role}</span></div>
                          <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]" onClick={() => transfer(ws.id, c.id)} data-testid={`close-account-transfer-${c.id}`}>
                            Transfer to this user
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <label className="flex items-center gap-2 text-xs" data-testid="close-account-delete-orphans">
                <input type="checkbox" checked={deleteOrphans} onChange={(e) => setDeleteOrphans(e.target.checked)} />
                Permanently delete the workspaces I still own (cascade — same as Danger zone delete).
              </label>
            </div>
          )}
          {owned.length === 0 && (
            <div className="text-sm text-foreground/70 border border-border p-3">You don&apos;t own any workspaces. Ready to close your account.</div>
          )}
          <div className="space-y-2 pt-3 border-t border-border">
            <Label className="label-eyebrow text-[10px] block">Type your email to confirm</Label>
            <Input value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder={user?.email} className="h-10 font-mono" data-testid="close-account-confirm-email" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)} data-testid="close-account-cancel">Cancel</Button>
            <Button
              variant="destructive"
              className="rounded-none"
              disabled={busy || confirmEmail.toLowerCase() !== (user?.email || "").toLowerCase() || (owned.length > 0 && !deleteOrphans)}
              onClick={closeAccount}
              data-testid="close-account-confirm"
            >
              {busy ? "Closing…" : "Close my account permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
